import styled, { css } from 'styled-components';

import { handleFontSize } from '@Components/molecules/WordBox/WordBox.styles';

import { TypeStatus } from '@Store/useTypingStore.types';

import { TypingTestFontSize } from '@Helpers/types/typing-test';

export const LineHeight = 1.625;

interface RootProps {
  readonly $fontFace: string;
  readonly $size: TypingTestFontSize;
  readonly $numberOfRows: number;
}

interface WordBoxTypedProps {
  readonly $wordStatus: TypeStatus;
  readonly $showErrorUnderline: boolean;
}

interface WordBoxErrorProps {
  readonly $wordStatus: TypeStatus;
  readonly $showErrorUnderline: boolean;
}

interface WordBoxActiveProps {
  readonly $direction: 'ltr' | 'rtl';
  readonly $isDisabled: boolean;
  readonly $isAnimated: boolean;
}

export const Root = styled.div<RootProps>`
  font-family: ${(props) => props.$fontFace};
  letter-spacing: 1.5px;
  font-weight: ${(props) => props.theme.font.typingBox.weight};
  font-kerning: none; // prevents small text jitter, especially in safari
  line-height: ${LineHeight};
  overflow: hidden;
  color: ${(props) => props.theme.palette.text.primary}; // todo: highContrast handling
  transition: color ${(props) => props.theme.transition.theme};
  padding-inline: 2px;
  margin-inline: -2px;
  white-space: pre-wrap;
  text-rendering: optimizeSpeed;

  font-size: ${(props) => handleFontSize(props.$size)};
  height: ${(props) => `calc(${props.$numberOfRows} * ${handleFontSize(props.$size)} * ${LineHeight})`};

  @media (max-width: ${(props) => props.theme.device.tablet}) {
    font-size: ${(props) => handleFontSize(props.$size, true)};
    height: ${(props) => `calc(${props.$numberOfRows} * ${handleFontSize(props.$size, true)} * ${LineHeight})`};
  }
`;

const getBoxShadow = (direction: 'ltr' | 'rtl', color: string) => {
  const shadow = direction === 'rtl' ? '2px' : '-2px';

  return css`
    box-shadow: ${shadow} 0 0 0 ${color};
  `;
};

export const WordBoxActive = styled.span<WordBoxActiveProps>`
  background-color: ${(props) => (props.$isDisabled ? 'transparent' : props.theme.palette.wordBox.active)};
  transition:
    background-color ${(props) => props.theme.transition.theme},
    color ${(props) => props.theme.transition.theme};
  color: ${(props) => props.theme.palette.text.primary};

  ${(props) => getBoxShadow(props.$direction, props.theme.palette.wordBox.cursor)}

  white-space: pre-wrap;
  position: relative;
  z-index: 1;

  @keyframes pulse {
    0% {
      ${(props) => getBoxShadow(props.$direction, props.theme.palette.wordBox.cursor)}
    }
    50% {
      ${(props) => getBoxShadow(props.$direction, 'transparent')}
    }
    100% {
      ${(props) => getBoxShadow(props.$direction, props.theme.palette.wordBox.cursor)}
    }
  }
  animation: ${(props) => (props.$isAnimated ? 'pulse 1s infinite' : 'unset')};
`;

const errorWordUnderline = css<WordBoxErrorProps | WordBoxTypedProps>`
  ${(props) =>
    props.$showErrorUnderline &&
    css`
      box-shadow: inset 0 -2px 0 0 ${props.theme.palette.wordBox.error};
    `}
`;

export const WordBoxError = styled.span<WordBoxErrorProps>`
  color: ${(props) => props.theme.palette.text.primary};
  background-color: ${(props) => props.theme.palette.wordBox.error};
  transition:
    background-color ${(props) => props.theme.transition.theme},
    color ${(props) => props.theme.transition.theme};
  ${errorWordUnderline}
`;

export const WordBoxTyped = styled.span<WordBoxTypedProps>`
  color: ${(props) => props.theme.palette.text.tertiary};
  background-color: ${(props) =>
    props.$wordStatus === TypeStatus.Active ? props.theme.palette.wordBox.typed : 'none'};
  transition:
    background-color ${(props) => props.theme.transition.theme},
    color ${(props) => props.theme.transition.theme};
  white-space: pre-wrap;
  ${errorWordUnderline}
`;
